/**
 * Manual Product Dashboard (User V2)
 * ====================================
 * 
 * Manual product entry form for adding custom products to cart.
 * Users can fill in product details and add them to the pre-request cart.
 * 
 * Workflow:
 * 1. User fills in product information
 * 2. Product is added to PreRequest collection
 * 3. User can continue shopping or proceed to checkout
 */

import React, { useState } from 'react';
import UserLayout from '../../layout/UserLayout';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getCategoriesForSelect } from '../../../../utils/categories';
import SuccessModal from '../../../SuccessModal';
import ConfirmationModal from '../../../ConfirmationModal';
import './ManualProductDashboard.scss';

interface ManualProductDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const ManualProductDashboard: React.FC<ManualProductDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();

  // Check if user's company is on basic plan
  const isBasicPlan = user?.company?.plan === 'basic';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    company_product_code: '1',
    dimensions: '',
    base_unit: '',
    color: '',
    category: '',
    description: '',
    quantity: 1,
    budget: 0,
    currency: 'TRY',
    size: ''
  });

  // Variant states
  const [variants, setVariants] = useState<Array<{ color: string; size: string; quantity: number; dimensions: string; company_product_code: string }>>([]);

  // Size options for variants
  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal states
  const [successModal, setSuccessModal] = useState({ isOpen: false, message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Cart state
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [showCart, setShowCart] = useState(false);

  // Get company name for dynamic label
  const companyName = typeof user?.company === 'string'
    ? user.company
    : user?.company?.name || 'Company';

  // Load cart items
  const loadCartItems = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PREREQUESTS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user._id
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCartItems(data.data.items || []);
        }
      }
    } catch (error) {
      console.error('Error loading cart items:', error);
    }
  };

  // Load cart on mount
  React.useEffect(() => {
    if (token && user._id) {
      loadCartItems();
    }
  }, [token, user._id]);

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' ? (value === '' ? '' : parseInt(value) || 0)
        : name === 'budget' ? parseFloat(value) || 0
          : value
    }));
  };

  const handleAddVariant = () => {
    setVariants([...variants, { color: '', size: '', quantity: 1, dimensions: '', company_product_code: '1' }]);
  };

  const handleRemoveVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleVariantChange = (index: number, field: 'color' | 'size' | 'quantity' | 'dimensions' | 'company_product_code', value: string | number) => {
    const updatedVariants = [...variants];
    if (field === 'quantity') {
      updatedVariants[index][field] = typeof value === 'number' ? value : parseInt(value) || 1;
    } else {
      updatedVariants[index][field] = value as string;
    }
    setVariants(updatedVariants);
  };

  // Handle form submission - Add to cart
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      setErrorModal({ isOpen: true, title: 'Validation Error', message: 'Product name is required' });
      return;
    }

    if (!formData.base_unit) {
      setErrorModal({ isOpen: true, title: 'Validation Error', message: 'Base unit is required' });
      return;
    }

    if (!formData.category) {
      setErrorModal({ isOpen: true, title: 'Validation Error', message: 'Category is required' });
      return;
    }

    // Validate variants - at least one of color, size, or dimensions must be filled
    if (variants.length > 0) {
      const invalidVariant = variants.find((v, index) => !v.color && !v.size && !v.dimensions);
      if (invalidVariant !== undefined) {
        const variantIndex = variants.findIndex(v => v === invalidVariant) + 1;
        setErrorModal({
          isOpen: true,
          title: 'Validation Error',
          message: `Variant ${variantIndex}: At least one of Color, Size, or Dimensions must be filled`
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Add main product to pre-request
      const response = await fetch(API_ENDPOINTS.PREREQUEST_ADD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user._id,
          'X-Company-ID': user.company_id
        },
        body: JSON.stringify({
          product_name: formData.name,
          product_brand: formData.brand,
          company_product_code: formData.company_product_code,
          dimensions: formData.dimensions,
          base_unit: formData.base_unit,
          color: formData.color,
          category: formData.category,
          description: formData.description,
          quantity: formData.quantity || 0,
          budget: isBasicPlan ? 0 : formData.budget,
          currency: formData.currency,
          size: formData.size,
          is_manual: true // Flag to indicate manual entry
        })
      });

      if (response.ok) {
        // If variants exist, add them too
        if (variants.length > 0) {
          for (const variant of variants) {
            await fetch(API_ENDPOINTS.PREREQUEST_ADD, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-User-ID': user._id,
                'X-Company-ID': user.company_id
              },
              body: JSON.stringify({
                product_name: formData.name,
                product_brand: formData.brand,
                company_product_code: variant.company_product_code || formData.company_product_code,
                dimensions: variant.dimensions || formData.dimensions,
                base_unit: formData.base_unit,
                color: variant.color || formData.color,
                category: formData.category,
                description: formData.description,
                quantity: variant.quantity || 1,
                budget: isBasicPlan ? 0 : formData.budget,
                currency: formData.currency,
                size: variant.size,
                is_manual: true
              })
            });
          }
        }

        setSuccessModal({ isOpen: true, message: 'Product added to cart successfully!' });

        // Reload cart items
        await loadCartItems();

        // Dispatch cart-updated event to refresh sidebar badge
        window.dispatchEvent(new Event('cart-updated'));

        // Reset form
        setFormData({
          name: '',
          brand: '',
          company_product_code: '1',
          dimensions: '',
          base_unit: '',
          color: '',
          category: '',
          description: '',
          quantity: 1,
          budget: 0,
          currency: 'TRY',
          size: ''
        });
        setVariants([]);
      } else {
        const errorData = await response.json();
        setErrorModal({ isOpen: true, title: 'Error', message: `Failed to add product: ${errorData.message || 'Unknown error'}` });
      }
    } catch (error) {
      console.error('Error adding manual product:', error);
      setErrorModal({ isOpen: true, title: 'Error', message: 'Failed to add product to cart' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cart modal (shared from CreateRequestDashboard)
  const CartModal = () => {
    if (!showCart) return null;

    return (
      <div className="cart-modal-overlay" onClick={() => setShowCart(false)}>
        <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cart-header">
            <h3>Shopping Cart ({cartItems.length})</h3>
            <button className="close-btn" onClick={() => setShowCart(false)}>×</button>
          </div>

          <div className="cart-content">
            {cartItems.length === 0 ? (
              <p className="empty-cart">Your cart is empty</p>
            ) : (
              <>
                <div className="cart-items">
                  {cartItems.map((item) => (
                    <div key={item.product_id || item.product_name} className="cart-item">
                      <div className="item-info">
                        {item.image_gridfs_id && (
                          <img
                            src={`http://localhost:5001/api/gridfs/${item.image_gridfs_id}`}
                            alt={item.product_name}
                            className="item-image"
                          />
                        )}
                        <div>
                          <h4>{item.product_name}</h4>
                          {item.is_manual && <span className="manual-badge">Manual</span>}
                        </div>
                      </div>
                      <div className="item-controls">
                        <span className="quantity-display">Qty: {item.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="cart-actions">
                  <button
                    className="submit-btn"
                    onClick={() => {
                      setShowCart(false);
                      onNavigate('/dashboard/user/create-request/marketplace');
                    }}
                  >
                    Go to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <UserLayout
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle="Add Manual Product"
        pageDescription="Fill in the product details to add a custom product to your cart"
        cartItemCount={cartItems.length}
        cartButton={{
          label: `🛒 ${cartItems.length}`,
          onClick: () => onNavigate('/dashboard/user/cart'),
          className: `cart-btn ${cartItems.length > 0 ? 'has-items' : ''}`
        }}
        headerActions={
          <button
            onClick={() => onNavigate('/dashboard/user/create-request/marketplace')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#fff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
          >
            ← Back to Marketplace
          </button>
        }
      >
        <div className="manual-product-dashboard">
          <form className="manual-product-form" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Product Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter product name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="brand">Brand</label>
                  <input
                    type="text"
                    id="brand"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder="Enter brand name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="quantity">Quantity *</label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="base_unit">Base Unit *</label>
                  <select
                    id="base_unit"
                    name="base_unit"
                    value={formData.base_unit}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select base unit</option>
                    <option value="adet">Adet (Piece)</option>
                    <option value="kg">Kilogram (kg)</option>
                    <option value="gr">Gram (gr)</option>
                    <option value="lt">Liter (lt)</option>
                    <option value="ml">Milliliter (ml)</option>
                    <option value="metre">Meter (m)</option>
                    <option value="mm">Millimeter (mm)</option>
                  </select>
                </div>
              </div>

              {/* Budget field - hidden for basic plan users */}
              {!isBasicPlan && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="budget">Budget</label>
                    <input
                      type="number"
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="currency">Currency</label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                    >
                      <option value="TRY">TRY</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select category</option>
                    {getCategoriesForSelect('tr').map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Product Specifications */}
            <div className="form-section">
              <h3>Product Specifications</h3>

              <div className="form-row form-row-single">
                <div className="form-group">
                  <label htmlFor="color">Color</label>
                  <input
                    type="text"
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="e.g., Red, Blue, Black"
                  />
                </div>
              </div>

              <div className="form-row form-row-single">
                <div className="form-group">
                  <label htmlFor="dimensions">Dimensions</label>
                  <input
                    type="text"
                    id="dimensions"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleChange}
                    placeholder="e.g., 10x20x5 cm"
                  />
                </div>
              </div>

              <div className="form-row form-row-single">
                <div className="form-group">
                  <label htmlFor="size">Size</label>
                  <select
                    id="size"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                  >
                    <option value="">Select Size (Optional)</option>
                    {sizeOptions.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row form-row-single">
                <div className="form-group">
                  <label htmlFor="company_product_code">{companyName} Code *</label>
                  <input
                    type="text"
                    id="company_product_code"
                    name="company_product_code"
                    value={formData.company_product_code}
                    onChange={handleChange}
                    placeholder={`${companyName} specific product code`}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Product Variants */}
            <div className="form-section">
              <div className="variants-header">
                <h3>Product Variants (Optional)</h3>
                <p className="variants-description">
                  Add color, size, quantity, dimensions and company code variants. They will share the same base information.
                </p>
              </div>

              {variants.map((variant, index) => (
                <div key={index} className="variant-item">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Color</label>
                      <input
                        type="text"
                        value={variant.color}
                        onChange={(e) => handleVariantChange(index, 'color', e.target.value)}
                        placeholder="e.g., Red, Blue, Black"
                      />
                    </div>
                    <div className="form-group">
                      <label>Size</label>
                      <select
                        value={variant.size}
                        onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                      >
                        <option value="">Select Size</option>
                        {sizeOptions.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Quantity</label>
                      <input
                        type="number"
                        value={variant.quantity}
                        onChange={(e) => handleVariantChange(index, 'quantity', e.target.value)}
                        min="1"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Dimensions</label>
                      <input
                        type="text"
                        value={variant.dimensions}
                        onChange={(e) => handleVariantChange(index, 'dimensions', e.target.value)}
                        placeholder="e.g., 10x20x5 cm"
                      />
                    </div>
                    <div className="form-group">
                      <label>{companyName} Code *</label>
                      <input
                        type="text"
                        value={variant.company_product_code}
                        onChange={(e) => handleVariantChange(index, 'company_product_code', e.target.value)}
                        placeholder="Company code"
                        required
                      />
                    </div>
                  </div>
                  <div className="variant-actions">
                    <button
                      type="button"
                      onClick={() => handleRemoveVariant(index)}
                      className="variant-remove-button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddVariant}
                className="add-variant-button"
              >
                + Add Variant
              </button>
            </div>

            {/* Description */}
            <div className="form-section">
              <h3>Additional Details</h3>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Additional product details, specifications, requirements..."
                  rows={4}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => onNavigate('/dashboard/user/create-request/marketplace')}
              >
                ← Back to Marketplace
              </button>

              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || !formData.name}
              >
                {isSubmitting ? 'Adding...' : 'Add to Cart'}
              </button>
            </div>
          </form>
        </div>
      </UserLayout>

      <CartModal />

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, message: '' })}
        message={successModal.message}
      />

      {/* Error/Confirmation Modal */}
      <ConfirmationModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
        onConfirm={() => setErrorModal({ isOpen: false, title: '', message: '' })}
        title={errorModal.title}
        message={errorModal.message}
        type="info"
        hideCancel={true}
        confirmText="OK"
      />
    </>
  );
};

export default ManualProductDashboard;

